import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ScopeFilter, TabButton, type Scope } from '../../Program/components/program tabs/InsightsPage';
import type { Person } from '../../Program/components/program tabs/insights/insightsData';
import type { CapabilityLevel } from '../../Program/components/learning-lifecycle/teamCapability';
import { apiMessage, capabilityApi, type AddMemberInput, type TeamChangeInput } from '../../../../../api/capability';
import {
  useCapabilityOperations,
  useCapabilityOrg,
  useCapabilityPairs,
  useRefreshCapability
} from '../../../../../queries/useCapability';
import { BODY, BRANDY_FRAME, SURFACE, TEXT_3 } from '../designSystem';
import { Bone } from '../../../../../components/ui/DashboardSkeletons';
import UpdateBehavior from './UpdateBehavior';
import UpdateTeam, { type OperationsPerson } from './UpdateTeam';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

type OperationsTab = 'behavior' | 'team';
const TABS: Array<{ key: OperationsTab; label: string }> = [
  { key: 'behavior', label: 'Manager Check' },
  { key: 'team', label: 'Team Check' }
];
const DIVIDE = { width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' };

/**
 * Operations: the hands-on work of keeping the Capability release true to the
 * team. Update Behavior records the capability level a manager has observed in
 * each employee on the job; Update Team keeps who is in the team, and in which
 * role, current. Both work on the teams picked in the filter: a team manager's
 * own teams, every team for an org admin. Everything is read from and saved to
 * the backend (/v3/admin/capability/operations, observations, teams).
 */
const OperationsPage: React.FC = () => {
  const [tab, setTab] = useState<OperationsTab>('behavior');
  const isMobileApp = useIsMobileApp();
  const org = useCapabilityOrg();
  const refresh = useRefreshCapability();
  // Every team this manager may change, with its people, roles, confirmations and observations.
  const { data: all, isLoading, isError, error } = useCapabilityOperations(null);
  const teamOptions = useMemo(() => (all?.teams ?? []).map((t) => ({ id: t.id, name: t.name })), [all]);
  const [chosen, setScope] = useState<Scope | null>(null);
  const scope: Scope = useMemo(() => chosen ?? teamOptions.map((t) => t.id), [chosen, teamOptions]);
  const teams = useMemo(
    () => (scope === 'org' ? teamOptions : teamOptions.filter((t) => scope.includes(t.id))),
    [scope, teamOptions]
  );
  const teamIds = useMemo(() => teams.map((t) => t.id), [teams]);
  // Everyone's level on each capability of their role, for Update Behavior.
  const { data: pairsView } = useCapabilityPairs(teamIds, teamIds.length > 0);
  const [failure, setFailure] = useState<string | null>(null);

  const inTeams = useMemo(() => new Set(teamIds), [teamIds]);
  const people: OperationsPerson[] = useMemo(
    () =>
      (all?.people ?? [])
        .filter((p) => inTeams.has(p.teamId))
        .map((p) => ({
          id: p.id,
          name: p.name,
          teamId: p.teamId,
          teamName: p.teamName,
          roleId: p.roleId,
          roleName: p.roleName || 'No Role Yet',
          invitePending: p.invitePending
        }))
        .sort((a, b) => a.teamName.localeCompare(b.teamName) || a.name.localeCompare(b.name)),
    [all, inTeams]
  );
  // Members of the org outside the teams in view, to add to one.
  const others = useMemo(() => {
    const here = new Set(people.map((p) => p.id));
    const fromTeams = (all?.people ?? [])
      .filter((p) => !here.has(p.id) && !p.invitePending)
      .map((p) => ({ id: p.id, name: p.name, teamName: p.teamName }));
    const outside = (all?.others ?? []).map((o) => ({ id: o.id, name: o.name, teamName: 'No Team' }));
    const seen = new Set<string>();
    return [...fromTeams, ...outside].filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
  }, [all, people]);
  const roles = useMemo(() => all?.roles ?? [], [all]);

  const run = async (work: () => Promise<unknown>) => {
    setFailure(null);
    try {
      await work();
      refresh('admin');
    } catch (err) {
      setFailure(apiMessage(err, 'That could not be saved. Try again.'));
      throw err;
    }
  };
  const confirmTeam = (changes: TeamChangeInput[]) => run(() => capabilityApi.confirmTeams(teamIds, changes, org));
  const addMember = (teamId: string, input: AddMemberInput) => run(() => capabilityApi.addMember(teamId, input, org));
  const observe = (personId: string, capabilityId: string, level: CapabilityLevel | null) =>
    run(() => capabilityApi.observe(personId, capabilityId, level, org));

  // Update Behavior asks about people who have joined, not those still invited.
  const active: Person[] = people.filter((p) => !p.invitePending);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: '#0F0F0F',
        // A phone: the page grows with its content and the app's scroll area scrolls it.
        [MOBILE_SX]: { height: 'auto', overflow: 'visible' }
      }}
    >
      <Box sx={{ flexShrink: 0, px: '25px', pt: '20px', [MOBILE_SX]: { px: 0, pt: 0 } }}>
        <Box
          role="tablist"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            p: '10px 14px',
            bgcolor: SURFACE,
            border: '1px solid #2A2A2A',
            borderRadius: '8px',
            // A phone: the team filter drops to a row of its own when the tabs fill the bar.
            [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '10px', p: '10px 12px' }
          }}
        >
          {TABS.map((item, i) => (
            <React.Fragment key={item.key}>
              {i > 0 && <Box sx={DIVIDE} />}
              <TabButton
                label={item.label}
                size={isMobileApp ? 15 : 17}
                active={tab === item.key}
                onClick={() => setTab(item.key)}
              />
            </React.Fragment>
          ))}
          <ScopeFilter teams={teamOptions} scope={scope} onChange={setScope} withOrganization={false} />
        </Box>
      </Box>
      <Box
        role="tabpanel"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          px: '25px',
          pt: '12px',
          pb: '20px',
          [MOBILE_SX]: { flex: 'none', px: 0, pb: 0 }
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            p: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            bgcolor: SURFACE,
            border: BRANDY_FRAME,
            borderRadius: '8px',
            boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
            [MOBILE_SX]: { overflow: 'visible', p: '12px' }
          }}
        >
          {failure && (
            <Typography role="alert" sx={{ ...BODY, color: '#FF6A55' }}>
              {failure}
            </Typography>
          )}
          {isLoading ? (
            <Box role="status" aria-label="Loading your teams" sx={{ display: 'flex', gap: '16px', minHeight: 320 }}>
              <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Bone key={i} height={44} radius="6px" />
                ))}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Bone width="40%" height={20} radius="4px" />
                <Bone width="75%" height={14} radius="4px" />
                {[0, 1, 2].map((i) => (
                  <Bone key={i} height={64} radius="6px" />
                ))}
              </Box>
            </Box>
          ) : isError ? (
            <Typography role="alert" sx={{ ...BODY, color: TEXT_3 }}>
              {apiMessage(error, 'Operations could not be loaded.')}
            </Typography>
          ) : tab === 'behavior' ? (
            <UpdateBehavior
              people={active}
              pairs={pairsView?.pairs ?? []}
              roles={roles}
              observed={all?.observed ?? {}}
              onObserve={observe}
            />
          ) : (
            <UpdateTeam
              teams={(all?.teams ?? []).filter((t) => inTeams.has(t.id))}
              people={people}
              others={others}
              roles={roles}
              onConfirm={confirmTeam}
              onAdd={addMember}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OperationsPage;
